import moment from 'moment'
import React, { useCallback, useContext, useRef } from 'react'

import BaseBookmarkToItineraryButton from './BaseBookmarkToItineraryButton'

import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import ModalContext from 'contexts/ModalContext'
import useToggle from 'hooks/useToggle'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { AddToItineraryDatesRequiredModal } from 'tripPlanner/components/Itinerary/AddToItineraryDatesRequiredModal'
import DatesDropdown from 'tripPlanner/components/Itinerary/DatesDropdown'
import DatesModal from 'tripPlanner/components/Itinerary/DatesModal'
import {
  itemIsInItinerary,
} from 'tripPlanner/components/Itinerary/utils'
import EditTripDates from 'tripPlanner/components/TripModal/Trips/EditTripDatesMC'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import { usePatchTripItem } from 'tripPlanner/hooks/api/tripItem'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import noop from 'lib/function/noop'

export interface Props {
  savedItem?: TripItem
  onRequestCreateItem: (date: moment.Moment) => void
  isCreatingItem: boolean
  tripId?: string
  variant: 'chip' | 'button'
  datesRequiredMessageVariation: React.ComponentProps<typeof AddToItineraryDatesRequiredModal>['variation']
  onSelectDate?: () => void
  dropdownPlacement?: React.ComponentProps<typeof DatesDropdown>['placement']
}

function BaseBookmarkToItinerary({
  savedItem,
  onRequestCreateItem,
  isCreatingItem,
  tripId: tripIdProp,
  variant,
  onSelectDate: onSelectDateProp = noop,
  dropdownPlacement,
}: Props) {
  const contextTripId = useTripId()
  const tripId = tripIdProp ?? contextTripId
  const { data: trip } = useTrip({ tripId })
  const showModal = useContext(ModalContext)
  const isMobile = useScreenSizeOnly('mobile')
  const { canEdit } = useTripPermissions(tripId)

  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const {
    value: dropdownOpen,
    toggle: toggleDropdown,
    off: closeDropdown,
  } = useToggle()

  const isVisibleInItinerary =
    !!savedItem && itemIsInItinerary(savedItem, trip)

  const { mutate: patchItem, isLoading: isUpdating } = usePatchTripItem()

  const addItemToItineraryWithDate = useCallback(
    async(date: moment.Moment) => {
      if (!trip || !savedItem) {
        return
      }

      patchItem(
        {
          tripId: trip.id,
          tripItemId: savedItem.id,
          tripItem: {
            type: savedItem.type,
            startDate: date.format(ISO_DATE_FORMAT),
          },
        },
        {
          onSuccess: () => {
            showSnackbar('Added to itinerary', 'success')
          },
        },
      )
    },
    [patchItem, trip, savedItem],
  )

  const removeFromItinerary = useCallback(async() => {
    closeDropdown()
    if (!trip || !savedItem) {
      return
    }

    patchItem(
      {
        tripId: trip.id,
        tripItemId: savedItem.id,
        tripItem: {
          type: savedItem.type,
          startDate: null,
          endDate: 'endDate' in savedItem ? null : undefined,
        },
      },
      {
        onSuccess: () => {
          showSnackbar('Removed from itinerary', 'success')
        },
      },
    )
  }, [closeDropdown, trip, savedItem, patchItem])

  const onSelectDate = useCallback(
    (date: moment.Moment) => {
      closeDropdown()
      if (savedItem) {
        addItemToItineraryWithDate(date)
      } else {
        onRequestCreateItem(date)
      }
      onSelectDateProp()
    },
    [
      addItemToItineraryWithDate,
      onRequestCreateItem,
      savedItem,
      closeDropdown,
      onSelectDateProp,
    ],
  )

  const openDateSelector = useCallback(
    async(tripStartDate: moment.Moment, tripEndDate: moment.Moment) => {
      if (isMobile) {
        const selection = await showModal<moment.Moment | null | undefined>(
          <DatesModal
            selectedDate={
              isVisibleInItinerary ? savedItem.startDate : undefined
            }
            startDate={tripStartDate}
            endDate={tripEndDate}
          />,
        )
        if (selection === null) {
          removeFromItinerary()
        } else if (moment.isMoment(selection)) {
          onSelectDate(selection)
        }
      } else {
        toggleDropdown()
      }
    },
    [
      isMobile,
      isVisibleInItinerary,
      onSelectDate,
      removeFromItinerary,
      savedItem?.startDate,
      showModal,
      toggleDropdown,
    ],
  )

  const onRequestAddTripDates = useCallback(async() => {
    const result = await showModal<{
      startDate: moment.Moment | undefined
      endDate: moment.Moment | undefined
    }>(<EditTripDates tripId={tripId} />)
    if (result?.startDate && result.endDate) {
      openDateSelector(result.startDate, result.endDate)
    }
  }, [openDateSelector, showModal, tripId])

  const onButtonClick = useCallback(async() => {
    if (!trip) {
      return
    } else if (!trip.startDate || !trip.endDate) {
      const didRequestAddTripDates = await showModal<boolean>(
        <AddToItineraryDatesRequiredModal variation="exploreItem" />,
      )
      if (didRequestAddTripDates) {
        onRequestAddTripDates()
      }
    } else {
      openDateSelector(trip.startDate, trip.endDate)
    }
  }, [
    trip,
    showModal,
    onRequestAddTripDates,
    openDateSelector,
  ])

  if (!canEdit || !tripId) {
    return null
  }

  const isProcessing = isCreatingItem || isUpdating

  return (
    <>
      <BaseBookmarkToItineraryButton
        itineraryDate={isVisibleInItinerary ? savedItem.startDate : undefined}
        onClick={onButtonClick}
        isProcessing={isProcessing}
        variant={variant}
        ref={triggerRef}
      />
      {trip?.startDate && trip.endDate && (
        <DatesDropdown
          isOpen={dropdownOpen}
          onClose={closeDropdown}
          onSelectDate={onSelectDate}
          onSelectRemoveDate={removeFromItinerary}
          selectedDate={isVisibleInItinerary ? savedItem.startDate : undefined}
          minDate={trip.startDate}
          maxDate={trip.endDate}
          triggerRef={triggerRef}
          portaled
          placement={dropdownPlacement}
        />
      )}
    </>
  )
}

export default BaseBookmarkToItinerary
